import { useState, useRef, useEffect } from 'react';
import { useWorkspace } from '../../hooks/useWorkspace';
import { REGIONS } from '@seedgovernance/shared';

export function WorkspaceSwitcher() {
  const { currentWorkspace, workspaces, switchWorkspace } = useWorkspace();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!currentWorkspace || workspaces.length <= 1) {
    return currentWorkspace ? (
      <div className="flex items-center gap-2">
        <span className="text-sm font-semibold text-white">
          {currentWorkspace.companyName}
        </span>
        <RegionBadge regionId={currentWorkspace.region} />
      </div>
    ) : null;
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-surface-light transition-colors"
      >
        <span className="text-sm font-semibold text-white">
          {currentWorkspace.companyName}
        </span>
        <RegionBadge regionId={currentWorkspace.region} />
        <svg className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="6,9 12,15 18,9" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute top-full start-0 mt-1 w-72 bg-surface-light rounded-lg border border-border shadow-xl py-1 z-50">
          <p className="px-3 py-2 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
            Switch workspace
          </p>
          {workspaces.map((ws) => (
            <button
              key={ws.tenantId}
              onClick={() => {
                switchWorkspace(ws.tenantId);
                setIsOpen(false);
              }}
              className={`w-full text-start px-3 py-2.5 flex items-center justify-between hover:bg-surface-mid transition-colors ${
                ws.tenantId === currentWorkspace.tenantId ? 'bg-brand/5' : ''
              }`}
            >
              <div>
                <p className="text-sm text-white">{ws.companyName}</p>
                <p className="text-xs text-slate-500 capitalize">{ws.role}</p>
              </div>
              <div className="flex items-center gap-2">
                <RegionBadge regionId={ws.region} />
                {ws.tenantId === currentWorkspace.tenantId && (
                  <svg className="w-4 h-4 text-growth" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="20,6 9,17 4,12" />
                  </svg>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function RegionBadge({ regionId }: { regionId: string }) {
  const region = REGIONS[regionId as keyof typeof REGIONS];
  return (
    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase bg-surface-dark text-slate-400 border border-border">
      {region?.id || regionId}
    </span>
  );
}
